import React from "react";
import { Box, Paper, Typography, useTheme, useMediaQuery } from "@mui/material";
import { InfoOutlined, ListAltOutlined } from "@mui/icons-material";
import { PieChart } from "@mui/x-charts/PieChart";
import { useDrawingArea } from "@mui/x-charts/hooks";
import { styled, alpha, darken, lighten } from "@mui/material/styles";
import { getChartColors, getChartStructuralStyles } from "theme";
import { HtmlTooltip } from "components/HeatMap";
import { appendTooltipGlossary } from "components/reports/shared/tooltipGlossary";

const StyledCenterText = styled("text")(({ theme }) => ({
  fill: theme.palette.text.secondary,
  textAnchor: "middle",
  dominantBaseline: "central",
  fontSize: 13,
  fontWeight: 600,
}));

function PieCenterLabel({ children }) {
  const { width, height, left, top } = useDrawingArea();
  return (
    <StyledCenterText x={left + width / 2} y={top + height / 2}>
      {children}
    </StyledCenterText>
  );
}

const getVariantColor = (baseColor, index) => {
  // Stronger alternating tints/shades so adjacent slices are visually distinct.
  const adjustments = [0, 0.22, -0.18, 0.34, -0.28, 0.44, -0.36, 0.14, -0.12];
  const amount = adjustments[index % adjustments.length];
  if (amount >= 0) {
    return lighten(baseColor, amount);
  }
  return darken(baseColor, Math.abs(amount));
};

/**
 * ReportNestedPieChart — Two-ring donut.
 * Inner ring = rolled-up category totals (Translation / Interpretation / Other).
 * Outer ring = individual service types within each category, shaded by opacity.
 *
 * @param {string} title
 * @param {Array}  serviceRows  - Array of { serviceType, category, revenue, ... }
 * @param {Function} valueFormatter
 * @param {number} height
 * @param {boolean} showInPaper
 * @param {Function} onCategoryClick - called with { category, revenue, rows[] }
 * @param {Function} onTypeClick     - called with the original serviceRow
 * @param {string} tooltipTitle
 * @param {string} tooltipContent
 */
const ReportNestedPieChart = ({
  title,
  serviceRows = [],
  valueFormatter = (v) => (v || 0).toLocaleString(),
  height = 360,
  showInPaper = true,
  onCategoryClick,
  onTypeClick,
  tooltipTitle,
  tooltipContent,
}) => {
  const theme = useTheme();
  const chartColors = getChartColors(theme);
  const resolvedTooltipContent = appendTooltipGlossary(tooltipContent);
  const filteredServiceRows = serviceRows.filter((row) => Number(row?.revenue) > 0);

  // Phones: scale both rings down so the donut fits the card (the 140px outer
  // radius = 280px diameter was clipped at 320–375px). Desktop keeps 55/115/140.
  const isMobile = !useMediaQuery("(min-width:600px)");
  const radiusScale = isMobile ? 0.68 : 1;
  const chartHeight = isMobile ? Math.min(height, 280) : height;

  // Assign a distinct base color per category
  const CATEGORY_COLORS = {
    Translation: chartColors.series[0],
    Interpretation: chartColors.series[1],
    Other: chartColors.series[3],
  };

  // Build inner ring — one slice per category
  const categoryMap = {};
  filteredServiceRows.forEach((row) => {
    const cat = row.category || "Other";
    if (!categoryMap[cat]) {
      categoryMap[cat] = {
        category: cat,
        revenue: 0,
        color: CATEGORY_COLORS[cat] || chartColors.series[Object.keys(categoryMap).length % chartColors.series.length],
        rows: [],
      };
    }
    categoryMap[cat].revenue += row.revenue || 0;
    categoryMap[cat].rows.push(row);
  });

  const categories = Object.values(categoryMap).sort((a, b) => b.revenue - a.revenue);

  const innerData = categories.map((cat, idx) => ({
    id: idx,
    label: cat.category,
    value: cat.revenue,
    color: cat.color,
  }));

  const innerTotalValue = innerData.reduce((sum, d) => sum + (Number(d.value) || 0), 0);

  const formatValueWithPercent = (rawValue, total) => {
    const numericValue = Number(rawValue) || 0;
    const formatted = valueFormatter(rawValue);
    if (!Number.isFinite(total) || total <= 0) return formatted;
    const percentage = ((numericValue / total) * 100).toFixed(1);
    return `${formatted} (${percentage}%)`;
  };

  const formatPercentLabel = (rawValue, total) => {
    const numericValue = Number(rawValue) || 0;
    if (!Number.isFinite(total) || total <= 0) return "";
    return `${((numericValue / total) * 100).toFixed(1)}%`;
  };

  // Build outer ring — individual types inside each category, shaded by rank
  const outerData = [];
  categories.forEach((cat) => {
    const sorted = [...cat.rows].sort((a, b) => b.revenue - a.revenue);
    sorted.forEach((row, i) => {
      const typeColor = getVariantColor(cat.color, i);
      outerData.push({
        id: `${cat.category}-${row.serviceType}`,
        label: row.serviceType,
        value: row.revenue,
        color: typeColor,
        _row: row,
        _category: cat,
      });
    });
  });

  // Tooltip legend — all types with their revenue, grouped by category
  const tooltipLegend = categories.flatMap((cat) =>
    cat.rows
      .slice()
      .sort((a, b) => b.revenue - a.revenue)
      .map((row, i) => ({
        label: `  ${row.serviceType}`,
        value: valueFormatter(row.revenue),
        color: getVariantColor(cat.color, i),
        isHeader: false,
      }))
      .reduce(
        (acc, item, i) => {
                if (i === 0) acc.push({ label: cat.category, value: formatValueWithPercent(cat.revenue, innerTotalValue), color: cat.color, isHeader: true });
          acc.push(item);
          return acc;
        },
        [],
      ),
  );

  const innerRadius = Math.round(55 * radiusScale);
  const outerInnerRadius = Math.round(115 * radiusScale);
  const outerOuterRadius = Math.round(140 * radiusScale);

  const outerTotalValue = outerData.reduce((sum, d) => sum + (Number(d.value) || 0), 0);

  const chartContent = (
    <Box>
      {title && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 2 }}>
          <Typography variant="h6" fontWeight="600">
            {title}
          </Typography>
          {resolvedTooltipContent && (
            <HtmlTooltip
              title={
                <Box sx={{ p: 1, maxHeight: "400px", overflowY: "auto" }}>
                  {tooltipTitle && (
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      {tooltipTitle}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                    {resolvedTooltipContent}
                  </Typography>
                </Box>
              }
              arrow
              placement="bottom-start"
            >
              <InfoOutlined
                sx={{
                  fontSize: 18,
                  color: theme.palette.primary.main,
                  cursor: "help",
                  opacity: 0.7,
                  "&:hover": { opacity: 1 },
                }}
              />
            </HtmlTooltip>
          )}
          {tooltipLegend.length > 0 && (
            <HtmlTooltip
              title={
                <Box sx={{ p: 1, maxHeight: "450px", overflowY: "auto", minWidth: 260 }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    All Service Types
                  </Typography>
                  {tooltipLegend.map((item, i) => (
                    <Box
                      key={i}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 0.4,
                        mt: item.isHeader && i > 0 ? 1 : 0,
                      }}
                    >
                      <Box
                        sx={{
                          width: item.isHeader ? 10 : 8,
                          height: item.isHeader ? 10 : 8,
                          borderRadius: "2px",
                          flexShrink: 0,
                          bgcolor: item.color,
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          flex: 1,
                          fontWeight: item.isHeader ? 600 : 400,
                          fontSize: item.isHeader ? "0.8rem" : "0.75rem",
                        }}
                      >
                        {item.label}
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={item.isHeader ? 600 : 400}
                        sx={{ whiteSpace: "nowrap", fontSize: item.isHeader ? "0.8rem" : "0.75rem" }}
                      >
                        {item.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              }
              arrow
              placement="bottom-start"
            >
              <ListAltOutlined
                sx={{
                  fontSize: 18,
                  color: theme.palette.text.secondary,
                  cursor: "help",
                  opacity: 0.7,
                  "&:hover": { opacity: 1 },
                }}
              />
            </HtmlTooltip>
          )}
        </Box>
      )}
      {filteredServiceRows.length === 0 ? (
        <Box
          sx={{
            height,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: theme.palette.background.default,
            borderRadius: "8px",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No data available
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <PieChart
            series={[
              {
                // Inner ring: categories
                innerRadius,
                outerRadius: outerInnerRadius,
                data: innerData,
                highlightScope: { fade: "global", highlight: "item" },
                highlighted: { additionalRadius: 5 },
                cornerRadius: 5,
                paddingAngle: 2,
                arcLabelMinAngle: 20,
                arcLabel: (item) => formatPercentLabel(item.value, innerTotalValue),
                valueFormatter: (item) => formatValueWithPercent(item.value, innerTotalValue),
              },
              {
                // Outer ring: individual service types
                innerRadius: outerInnerRadius + 3,
                outerRadius: outerOuterRadius,
                data: outerData,
                highlightScope: { fade: "global", highlight: "item" },
                highlighted: { additionalRadius: 3 },
                cornerRadius: 3,
                paddingAngle: 1,
                arcLabelMinAngle: 16,
                arcLabel: (item) => formatPercentLabel(item.value, outerTotalValue),
                valueFormatter: (item) => formatValueWithPercent(item.value, outerTotalValue),
              },
            ]}
            onItemClick={(event, d) => {
              if (d.seriesIndex === 0) {
                const cat = categories[d.dataIndex];
                if (cat && onCategoryClick) onCategoryClick({ category: cat.category, revenue: cat.revenue, rows: cat.rows });
              } else {
                const item = outerData[d.dataIndex];
                if (item && onTypeClick) onTypeClick(item._row);
              }
            }}
            height={chartHeight}
            margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
            slotProps={{ legend: { hidden: true } }}
            sx={{
              ...getChartStructuralStyles(theme),
              "& .MuiPieArcLabel-root": {
                fill: theme.palette.background.default,
                fontWeight: 700,
              },
              "& .MuiPieArc-root": {
                stroke: alpha(theme.palette.background.paper, 0.95),
                strokeWidth: 1.5,
                ...(onCategoryClick || onTypeClick ? { cursor: "pointer" } : {}),
              },
            }}
          >
            <PieCenterLabel>Mix</PieCenterLabel>
          </PieChart>
        </Box>
      )}
    </Box>
  );

  if (showInPaper) {
    return (
      <Paper
        sx={{
          p: { xs: 1.5, sm: 3 },
          bgcolor: theme.palette.background.default,
          border: `1px solid ${theme.palette.primary.border2}`,
          borderRadius: "12px",
        }}
      >
        {chartContent}
      </Paper>
    );
  }

  return chartContent;
};

export default ReportNestedPieChart;
